import type { ComponentChildren } from 'preact';
import { FileText, Heart, ScrollText, ShieldCheck, Users } from 'lucide-preact';
import { openModal, replace, route } from '../state/nav';
import { Modal, Screen, Tabs } from '../ui/kit';

export type LegalDocId = 'privacy' | 'terms' | 'parents' | 'credits';

const TITLES: Record<LegalDocId, string> = {
  privacy: 'Privacy Policy',
  terms: 'Terms of Service',
  parents: "Parents' Guide",
  credits: 'Credits',
};

export const SUPPORT_EMAIL = 'support@voxelmarines.com';
const UPDATED = 'October 22, 2026';

function Mail() {
  return <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>;
}

function Section(props: { title: string; children: ComponentChildren }) {
  return (
    <section class="legal-section">
      <h3>{props.title}</h3>
      {props.children}
    </section>
  );
}

function Privacy() {
  return (
    <>
      <p class="legal-lead">We made Voxel Marines to be fun and safe. This page explains what information the game uses and why — in plain words.</p>
      <p class="legal-updated">Last updated {UPDATED}</p>
      <Section title="What we collect">
        <ul>
          <li>
            <b>A random device ID</b> so your progress stays with you.
          </li>
          <li>
            <b>The year you were born</b> — only to decide whether your account uses our extra-safe settings for players under 13. We keep the year and that
            setting, nothing else.
          </li>
          <li>
            <b>Game stats and progress</b> like levels, matches, takedowns, coins, gems and the items you unlock.
          </li>
          <li>
            <b>How you use the game</b>: which screens you open, when you play and for how long, which matches you start and finish, and how far you get in Boot
            Camp. This shows us what's fun and where players get stuck.
          </li>
          <li>
            <b>Basic device info</b>: the kind of device (phone, tablet or computer), its operating system and browser, the game version, your language setting,
            and how smoothly the game runs (frame rate and loading time). We guess your country from your language setting — never from your location.
          </li>
          <li>
            <b>Crash reports</b>: the error message and where in the game's code it happened, so we can fix bugs.
          </li>
          <li>
            <b>How you found the game</b>: the website that sent you here or the campaign name in the link you used (for example, a video or an ad).
          </li>
          <li>
            <b>Chat messages</b> in every channel — global, clan, party and private messages. Chat is filtered automatically, and messages are saved for 30 days
            so moderators can review reports. Then they are deleted.
          </li>
          <li>
            <b>Things you create</b>, like custom skins and clan names. Other players can see them, and moderators check them.
          </li>
          <li>
            <b>Short match replays</b>: when something big happens in a public match (like a triple takedown or a building collapse), the server saves the 20 to
            30 seconds around it: how the marines moved and what got blown up, never chat. We use them to make videos about the game and may show them on our
            website, and the player who made the moment can watch it and share a link to it. Names never appear in videos, on the website or in shared links:
            players show up as "Marine 1", "Marine 2" and so on. Players under 13 are never the featured player and can't share links.
          </li>
          <li>
            <b>Purchases</b>: which gem pack you bought, the price, the store and the receipt number. The App Store, Google Play or our card processor handles the
            payment itself — we never see your card details.
          </li>
          <li>
            <b>An email address</b> only if a player aged 13 or older chooses to link one to back up their account. We use it to log in and to send a link if you forget your password, nothing else: no newsletters or ads.
          </li>
        </ul>
      </Section>
      <Section title="What we never collect">
        <p>
          We don't ask for your real name, photos, contacts, or precise location, and chat blocks emails, phone numbers and links. Our servers see your internet
          address only to block abuse, and we don't store it.
        </p>
      </Section>
      <Section title="Players under 13">
        <p>
          Accounts for players under 13 use restricted mode: preset quick-chat messages only, call signs picked for them, no purchases on the web, and no
          personalized ads. Their usage and crash reports are anonymous — not linked to the account. The account only keeps its game progress, the basic device
          info and how the game was found from when it was created. We don't knowingly collect more personal information from children than the game needs to
          work.
        </p>
      </Section>
      <Section title="How we use information">
        <p>
          To run matches, save progress, keep the game fair and friendly (anti-cheat and moderation), fix bugs and crashes, balance the game, understand which
          parts are fun, see which websites and campaigns bring new players, and make videos about the game from match replays. Moderators can read chat logs and see game data when they look into a report or
          help a player.
        </p>
      </Section>
      <Section title="Who we share it with">
        <ul>
          <li>We never sell your information, and we don't use outside analytics or advertising companies to track you.</li>
          <li>Our hosting provider runs the servers that store the game's data.</li>
          <li>Our email service (Resend) delivers password reset emails and the emails you send us, so it sees those addresses and messages.</li>
          <li>Apple, Google and our card processor handle purchases under their own privacy policies.</li>
        </ul>
      </Section>
      <Section title="How long we keep it">
        <ul>
          <li>Chat messages: 30 days.</li>
          <li>Usage details and crash reports: 90 days.</li>
          <li>Match replays: up to 60 days (shared links stop working then), unless we keep one for a video or the website.</li>
          <li>Your account and progress: until you delete your account.</li>
          <li>Purchase records: kept for accounting and refunds, even after an account is deleted.</li>
          <li>Emails you send us: a year after we last wrote back and forth (spam: 30 days).</li>
        </ul>
      </Section>
      <Section title="Your choices">
        <ul>
          <li>
            You can delete your account any time in Settings → Account, or on the web at voxelmarines.com/delete-account. This removes your profile, progress,
            friends and chat messages, and unlinks your usage and crash reports from you.
          </li>
          <li>You can ask us for a copy of the information linked to your account.</li>
          <li>Parents and guardians can ask us to review, copy or delete their child's information.</li>
        </ul>
      </Section>
      <Section title="Changes">
        <p>If this policy changes in an important way, we'll tell you in the game before the change takes effect.</p>
      </Section>
      <Section title="Contact us">
        <p>
          Questions, or a request to copy or delete information? Email <Mail />. Parents: include your child's call sign or friend code (shown on the Profile
          screen) so we can find the account.
        </p>
      </Section>
    </>
  );
}

function Terms() {
  return (
    <>
      <p class="legal-lead">Welcome to the squad! By playing Voxel Marines you agree to these rules.</p>
      <p class="legal-updated">Last updated {UPDATED}</p>
      <Section title="Play fair, be kind">
        <ul>
          <li>No cheating, hacking, exploiting bugs, or using programs that give an unfair advantage.</li>
          <li>No bullying, hate, threats, or trying to upset other players.</li>
          <li>Never share personal information — yours or anyone else's.</li>
          <li>Keep names, clan names and custom skins friendly for everyone.</li>
        </ul>
      </Section>
      <Section title="Your account">
        <p>Keep your login safe. Guest progress lives on this device until you link an email (players 13 and older).</p>
      </Section>
      <Section title="Virtual items">
        <p>
          Coins, gems and items only exist inside the game and have no real-world value. Weapons are earned by playing; gems buy cosmetics, the premium battle
          pass and name changes — never extra power.
        </p>
      </Section>
      <Section title="Purchases">
        <p>
          Gem packs are sold through the App Store, Google Play or our secure web checkout, and follow their refund rules. Players under 13 can't buy on the
          web.
        </p>
      </Section>
      <Section title="Breaking the rules">
        <p>We may mute, suspend or remove accounts that break these rules. You can report players from their profile or from chat.</p>
      </Section>
      <Section title="Contact us">
        <p>Questions, or think we got something wrong? Email <Mail />.</p>
      </Section>
    </>
  );
}

function Parents() {
  return (
    <>
      <p class="legal-lead">
        Voxel Marines is a colorful team shooter where everything — including the marines — is built from blocks. When a marine is defeated, they shatter into
        voxels and rebuild. There is no blood or gore.
      </p>
      <Section title="Safety features">
        <ul>
          <li>
            <b>Restricted mode</b> for players under 13: preset quick-chat only, generated call signs, no web purchases, non-personalized ads only.
          </li>
          <li>
            <b>Filtered chat</b> for everyone. Personal info like emails, phone numbers and links is blocked.
          </li>
          <li>
            <b>Block, mute and report</b> are one tap away on every player and chat message.
          </li>
          <li>
            <b>Moderators</b> review reports, custom skins and clan names. Chat is saved for 30 days so reports can be checked, then deleted.
          </li>
          <li>
            <b>Chat can be hidden</b> completely in Settings → Social.
          </li>
        </ul>
      </Section>
      <Section title="Spending">
        <ul>
          <li>Power is earned, looks are bought: weapons unlock with coins earned by playing. Money can't buy stronger gear.</li>
          <li>No paid random loot boxes — you always see exactly what you get.</li>
          <li>On phones and tablets, purchases go through the App Store or Google Play, so Ask to Buy and Family Link approvals apply.</li>
        </ul>
      </Section>
      <Section title="Healthy play">
        <p>Matches last 5–10 minutes, so it's easy to agree on "one more match" limits. Daily rewards don't punish missed days beyond resetting the streak.</p>
      </Section>
      <Section title="Your child's data">
        <p>
          Your child's usage and crash reports are anonymous. You can delete the account in Settings → Account → Delete account, or email <Mail /> to review,
          copy or delete your child's information.
        </p>
      </Section>
    </>
  );
}

function Credits() {
  return (
    <>
      <p class="legal-lead">Voxel Marines is built by a small team that loves blowing up blocks.</p>
      <Section title="Made with">
        <ul>
          <li>Three.js — 3D rendering</li>
          <li>Preact &amp; Preact Signals — interface</li>
          <li>Lucide — icons (ISC license)</li>
          <li>Lilita One &amp; Nunito — fonts (SIL Open Font License)</li>
          <li>Hono &amp; Kysely — game services</li>
          <li>Every sound is synthesized live in your browser — no audio files.</li>
        </ul>
      </Section>
      <Section title="Thanks">
        <p>To every playtester who knocked down a clock tower just to see what happens. You're the best.</p>
      </Section>
    </>
  );
}

export function LegalBody(props: { doc: LegalDocId }) {
  return (
    <div class="legal-body">
      {props.doc === 'privacy' && <Privacy />}
      {props.doc === 'terms' && <Terms />}
      {props.doc === 'parents' && <Parents />}
      {props.doc === 'credits' && <Credits />}
    </div>
  );
}

export function openLegal(doc: LegalDocId): void {
  openModal((close) => (
    <Modal title={TITLES[doc]} icon={<ScrollText size={22} />} size="lg" onClose={close} class="legal-modal">
      <LegalBody doc={doc} />
    </Modal>
  ));
}

export function LegalScreen() {
  const doc = (route.value.params.doc as LegalDocId) || 'privacy';
  return (
    <Screen
      title="Legal & Info"
      icon={<FileText size={26} />}
      right={<span />}
      tabs={
        <Tabs<LegalDocId>
          value={doc}
          onChange={(d) => replace('legal', { doc: d })}
          tabs={[
            { id: 'privacy', label: 'Privacy', icon: <ShieldCheck size={18} /> },
            { id: 'terms', label: 'Terms', icon: <ScrollText size={18} /> },
            { id: 'parents', label: 'Parents', icon: <Users size={18} /> },
            { id: 'credits', label: 'Credits', icon: <Heart size={18} /> },
          ]}
        />
      }
    >
      <div class="legal-page" key={doc}>
        <h2>{TITLES[doc]}</h2>
        <LegalBody doc={doc} />
      </div>
    </Screen>
  );
}
